/**
 * Create Modal
 * Modal for creating Rituals, Coaching Sessions, or Communities
 * Uses shadcn Dialog pattern
 */

import { CoachingIcon, CommunityCreateIcon, RitualsIcon } from "@/components/icons"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/language"

interface CreateModalProps {
  isOpen: boolean
  onClose: () => void
  onNavigate?: (path: string) => void
}

interface CreateOption {
  id: string
  icon: React.ReactNode
  title: string
  description: string
  action: () => void
}

const buttonColors: Record<string, { bg: string; iconBg: string; iconColor: string }> = {
  ritual: { bg: "#C9A8761A", iconBg: "#C9A87626", iconColor: "#C9A876" },
  coaching: { bg: "#B8B8961A", iconBg: "#B8B89626", iconColor: "#B8B896" },
  community: { bg: "#C867571A", iconBg: "#C8675726", iconColor: "#C86757" },
}

export function CreateModal({ isOpen, onClose, onNavigate }: CreateModalProps) {
  const { t } = useTranslation("common")

  const createOptions: CreateOption[] = [
    {
      id: "ritual",
      icon: <RitualsIcon className="w-6 h-6" />,
      title: t("createModal.ritual"),
      description: t("createModal.ritualDescription"),
      action: () => {
        onClose()
        onNavigate?.("/rituals/create")
      },
    },
    {
      id: "coaching",
      icon: <CoachingIcon className="w-6 h-6" />,
      title: t("createModal.coaching"),
      description: t("createModal.coachingDescription"),
      action: () => {
        onClose()
        onNavigate?.("/coaching-sessions/create")
      },
    },
    {
      id: "community",
      icon: <CommunityCreateIcon className="w-6 h-6" />,
      title: t("createModal.community"),
      description: t("createModal.communityDescription"),
      action: () => {
        onClose()
        onNavigate?.("/communities/create")
      },
    },
  ]

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="md:w-[680px]" overlayClass="bg-[#020202] md:bg-[rgb(0,0,0,0.9)]">
        <DialogHeader className="text-center">
          <DialogTitle className="text-primary text-[24px] font-semibold text-center mt-7 !mb-0">
            {t("createModal.title")}
          </DialogTitle>
          <DialogDescription className="text-primary text-center text-sm leading-6.25 sm:px-15 md:px-30 mt-4">
            {t("createModal.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 px-1">
          {createOptions.map(option => {
            const colors = buttonColors[option.id]
            return (
              <div
                key={option.id}
                className="flex md:items-center justify-center gap-3.75 md:gap-4 min-h-[95px] md:h-[95px] p-3.75 md:px-4 text-left rounded-xl border cursor-pointer"
                style={{ backgroundColor: colors.bg, borderColor: colors.bg }}
                onClick={option.action}
              >
                <div
                  className="w-10 h-10 md:w-15 md:h-15 rounded-full flex items-center justify-center flex-shrink-0 p-2 md:p-0"
                  style={{ backgroundColor: colors.iconBg, color: colors.iconColor }}
                >
                  {option.icon}
                </div>
                <div className="flex-1">
                  <span className="block font-semibold text-primary text-sm md:text-[16px]">
                    {option.title}
                  </span>
                  <span className="block text-xs leading-5 sm:text-[14px] text-stone mt-1.25">
                    {option.description}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
